"use client";

import { useRef, useState } from "react";
import {
  AnimatePresence,
  LayoutGroup,
  motion,
  useInView,
  useReducedMotion,
  type Transition,
} from "framer-motion";
import { faqItems, type FaqItem } from "@/data/faq";
import { sectionSurface, type SectionVariant } from "@/lib/section-theme";
import { FaChevronDown } from "react-icons/fa";
import type { ReactNode } from "react";

const HERO_GRADIENT_TEXT =
  "bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 bg-clip-text text-transparent";

const LAYOUT_ANIMATION = { duration: 0.38, ease: [0.4, 0, 0.2, 1] as const };

const CONTENT_TRANSITION: Transition = {
  duration: 0.32,
  ease: [0.4, 0, 0.2, 1],
};

const SEQUENCE_EASE = [0.16, 1, 0.3, 1] as const;
const ENTRANCE_DURATION = 0.55;
const CARD_STAGGER = 0.1;
const GRID_DELAY = 0.16;

function entranceTransition(
  prefersReducedMotion: boolean | null,
  delay = 0,
  duration = ENTRANCE_DURATION,
): Transition {
  if (prefersReducedMotion) return { duration: 0 };
  return { delay, duration, ease: SEQUENCE_EASE };
}

export function FaqSection({
  variant = "dark",
  items = faqItems,
  title,
  subtitle,
  singleColumn = false,
}: {
  variant?: SectionVariant;
  items?: FaqItem[];
  title?: ReactNode;
  subtitle?: string;
  singleColumn?: boolean;
}) {
  const theme = sectionSurface(variant);
  const isLight = variant === "light";
  const prefersReducedMotion = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section ref={sectionRef} className={`${theme.sectionPlain} py-14 sm:py-16`}>
      <div className="mx-auto flex max-w-6xl flex-col gap-8 px-6 sm:gap-10">
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-3 text-center sm:gap-4">
          <motion.div
            initial={false}
            animate={
              isInView
                ? { opacity: 1, y: 0, filter: "blur(0px)" }
                : { opacity: 0, y: 14, filter: "blur(12px)" }
            }
            transition={entranceTransition(prefersReducedMotion, 0, 0.62)}
          >
            <h2
              className={`mx-auto max-w-4xl text-balance text-[1.5rem] font-bold leading-[1.08] tracking-[-0.04em] sm:text-3xl md:text-4xl md:leading-[1.05] ${
                isLight ? "text-[#1C1C1C]" : "text-white"
              }`}
            >
              {title ?? (
                <>
                  Questions <span className={HERO_GRADIENT_TEXT}>fréquentes</span>
                </>
              )}
            </h2>
          </motion.div>
          {(subtitle ?? (title ? null : "Des réponses simples pour vous aider à décider en toute confiance.")) && (
          <motion.p
            className={`text-sm font-medium sm:text-base ${
              isLight ? "text-zinc-500" : "text-white/70"
            }`}
            initial={false}
            animate={
              isInView
                ? { opacity: 1, y: 0, filter: "blur(0px)" }
                : { opacity: 0, y: 8, filter: "blur(8px)" }
            }
            transition={entranceTransition(prefersReducedMotion, 0.12, 0.52)}
          >
            {subtitle ?? "Des réponses simples pour vous aider à décider en toute confiance."}
          </motion.p>
          )}
        </div>

        <LayoutGroup id="faq-grid">
          <div
            className={`grid grid-cols-1 gap-3 sm:gap-4 md:items-start ${
              singleColumn ? "max-w-3xl mx-auto w-full" : "md:grid-cols-2"
            }`}
          >
            {items.map((item, index) => {
              const isOpen = openIndex === index;

              return (
                <motion.div
                  key={item.question}
                  layout={!prefersReducedMotion}
                  initial={false}
                  animate={
                    isInView
                      ? { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }
                      : { opacity: 0, y: 22, scale: 0.98, filter: "blur(10px)" }
                  }
                  transition={
                    prefersReducedMotion
                      ? { duration: 0 }
                      : {
                          layout: LAYOUT_ANIMATION,
                          opacity: {
                            delay: GRID_DELAY + index * CARD_STAGGER,
                            duration: ENTRANCE_DURATION,
                            ease: SEQUENCE_EASE,
                          },
                          y: {
                            delay: GRID_DELAY + index * CARD_STAGGER,
                            duration: ENTRANCE_DURATION,
                            ease: SEQUENCE_EASE,
                          },
                          scale: {
                            delay: GRID_DELAY + index * CARD_STAGGER,
                            duration: ENTRANCE_DURATION,
                            ease: SEQUENCE_EASE,
                          },
                          filter: {
                            delay: GRID_DELAY + index * CARD_STAGGER,
                            duration: ENTRANCE_DURATION,
                            ease: SEQUENCE_EASE,
                          },
                        }
                  }
                  className={`${theme.faqCard} flex flex-col overflow-hidden`}
                >
                  <button
                    type="button"
                    onClick={() => toggleQuestion(index)}
                    aria-expanded={isOpen}
                    className="flex w-full cursor-pointer items-start justify-between gap-3 p-4 text-left sm:gap-4 sm:p-5"
                  >
                    <h3 className={`${theme.faqQuestion} text-[0.95rem] leading-snug sm:text-base`}>
                      {item.question}
                    </h3>
                    <motion.div
                      layout={!prefersReducedMotion}
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: prefersReducedMotion ? 0 : 0.25 }}
                      className={theme.faqChevron}
                    >
                      <FaChevronDown className={theme.faqChevronIcon} />
                    </motion.div>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="answer"
                        initial={
                          prefersReducedMotion ? false : { height: 0, opacity: 0 }
                        }
                        animate={{ height: "auto", opacity: 1 }}
                        exit={
                          prefersReducedMotion ? undefined : { height: 0, opacity: 0 }
                        }
                        transition={prefersReducedMotion ? { duration: 0 } : CONTENT_TRANSITION}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                          <div className={`border-t pt-3 ${theme.divider}`}>
                            <p className={`${theme.faqAnswer} text-[0.88rem] sm:text-sm`}>
                              {item.answer}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </LayoutGroup>
      </div>
    </section>
  );
}
